import { useState, type RefObject } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, Text } from "@ferrivia/ui";
import { useMarket, useTranslation } from "@ferrivia/i18n";
import { useQuestionnaireCopy } from "@ferrivia/questionnaire-ui";
import {
  optionsForMarket,
  reachableGroupFields,
  type AnswerValue,
  type ItemFieldMap,
  type QuestionGroupField,
  type RepeatingQuestionDefinition,
  type SingleQuestionDefinition,
} from "@ferrivia/api-contract";
import { BooleanQuestion } from "./BooleanQuestion";
import { SingleChoiceQuestion } from "./SingleChoiceQuestion";
import { MultiChoiceQuestion } from "./MultiChoiceQuestion";
import { TextQuestion } from "./TextQuestion";
import { NumberQuestion } from "./NumberQuestion";
import { DateQuestion } from "./DateQuestion";
import { ConditionalReveal } from "./ConditionalReveal";
import { SupplementPicker } from "./SupplementPicker";
import { PhotoUploadField } from "./PhotoUploadField";
import f from "./fieldBox.module.css";
import s from "./GroupItemFields.module.css";

// Matches useQuestionnaire.ts's own ITEM_FIELD_KEY_SEPARATOR -- kept as a
// literal here rather than a shared constant because the two modules don't
// share a dependency edge for it, and a stray colon can't appear in a
// question/field key (lower_snake_case) or an item index (a plain number).
export const ITEM_FIELD_KEY_SEPARATOR = ":";

// A repeating-group field shares kind/options/showIf/integer/min/max with a
// top-level question but not `repeating` -- SingleChoiceQuestion,
// MultiChoiceQuestion and NumberQuestion all take a SingleQuestionDefinition,
// so this widens the field into that shape rather than duplicating their
// rendering. `key` is overridden to a per-item-unique string:
// SingleChoiceQuestion/MultiChoiceQuestion use `question.key` as a native
// radio/checkbox group `name`, and two items sharing the same field key would
// otherwise merge their radios into one native group.
// `control` is dropped: the group's and the single question's controls are
// different vocabularies, and GroupItemFields handles the group's itself.
function asSingleQuestion(field: QuestionGroupField, uniqueKey: string): SingleQuestionDefinition {
  const { control: _control, ...rest } = field;
  return { ...rest, key: uniqueKey, repeating: false };
}

export interface GroupItemFieldsProps {
  group: RepeatingQuestionDefinition;
  item: ItemFieldMap;
  /** The item's wire index, or null for an item not saved yet -- which has
      no server-side errors to show. */
  itemIndex: number | null;
  /** `useQuestionnaire`'s `invalidKeys`, keyed by its `itemFieldKey`. */
  invalidKeys: ReadonlySet<string>;
  onFieldChange(fieldKey: string, value: AnswerValue): void;
  scrollContainer: RefObject<HTMLElement | null>;
}

/** One repeating-group item's fields, rendered with the same per-kind
    components `QuestionBlock` dispatches to for a top-level question.

    An item-scoped `showIf` (e.g. `stopped_at` gated on `still_using ===
    false`) resolves against `item` via `reachableGroupFields` -- never the
    submission's top-level answers. */
export function GroupItemFields({
  group,
  item,
  itemIndex,
  invalidKeys,
  onFieldChange,
  scrollContainer,
}: GroupItemFieldsProps) {
  const { t } = useTranslation("client");
  const market = useMarket();
  const copy = useQuestionnaireCopy();
  const groupKey = group.key;
  const reachable = new Set(reachableGroupFields(group, item).map((field) => field.key));

  // The picker writes `name` and its `writtenByControl` sibling
  // (`product_id`) together (see the field comments in questionnaires.ts).
  // The field immediately after that sibling, if it's a number, is the one
  // the picker's dose hint attaches to (`daily_iron_dose` today) -- found by
  // position rather than a hardcoded key, so a future group can opt in by
  // ordering its fields the same way, without this component knowing its key.
  const productIdField = group.fields.find((field) => field.writtenByControl);
  const productIdFieldIndex = productIdField ? group.fields.indexOf(productIdField) : -1;
  const doseHintField = group.fields[productIdFieldIndex + 1];
  const doseHintFieldKey = productIdField && doseHintField?.kind === "number" ? doseHintField.key : undefined;
  const [doseHint, setDoseHint] = useState<string | undefined>(undefined);

  // Same tree-by-showIf shape SectionScreen builds for a section's own
  // questions: a field whose showIf targets another field in this group
  // becomes that field's child, so it can nest and collapse with
  // ConditionalReveal instead of popping in and out of a flat list.
  const fieldKeys = new Set(group.fields.map((field) => field.key));
  const childrenByParent = new Map<string, QuestionGroupField[]>();
  const roots: QuestionGroupField[] = [];
  for (const field of group.fields) {
    // `writtenByControl` fields (`product_id`) are never rendered or
    // answered on their own -- the picker writes them alongside `name`.
    if (field.writtenByControl) continue;
    const parentKey = field.showIf?.key;
    if (parentKey && fieldKeys.has(parentKey)) {
      const siblings = childrenByParent.get(parentKey) ?? [];
      siblings.push(field);
      childrenByParent.set(parentKey, siblings);
    } else {
      roots.push(field);
    }
  }

  function renderField(field: QuestionGroupField) {
    const value = item.get(field.key);
    const uniqueKey = `${groupKey}-${itemIndex ?? "new"}-${field.key}`;
    const fieldLabelId = `${uniqueKey}-label`;
    const fieldCopy = copy.groupFieldFor(groupKey, field.key);
    const label = fieldCopy?.label ?? field.key;
    const optionLabelFor = (optionId: string) => copy.groupFieldOptionLabelFor(groupKey, field.key, optionId) ?? optionId;
    const invalidKey = itemIndex === null ? null : [groupKey, itemIndex, field.key].join(ITEM_FIELD_KEY_SEPARATOR);
    const errorText = invalidKey !== null && invalidKeys.has(invalidKey) ? t("questionnaire.invalidAnswer", "Tarkista vastaus.") : undefined;

    function change(next: AnswerValue) {
      onFieldChange(field.key, next);
    }

    function renderControl() {
      switch (field.kind) {
        case "boolean":
          return (
            <BooleanQuestion
              labelledBy={fieldLabelId}
              value={value?.kind === "boolean" ? value.value : undefined}
              onChange={(next) => change({ kind: "boolean", value: next })}
            />
          );
        case "choice":
          if (field.control === "select") {
            return (
              <Select
                value={value?.kind === "choice" ? value.value : undefined}
                onValueChange={(optionId) => change({ kind: "choice", value: optionId })}
              >
                <SelectTrigger
                  className={f.box}
                  aria-labelledby={fieldLabelId}
                  placeholder={t("questionnaire.selectPlaceholder", "Valitse")}
                />
                <SelectContent className={s.selectContent}>
                  {optionsForMarket(field.options ?? [], market).map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {optionLabelFor(option.id)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );
          }
          return (
            <SingleChoiceQuestion
              question={asSingleQuestion(field, uniqueKey)}
              labelledBy={fieldLabelId}
              optionLabelFor={optionLabelFor}
              value={value?.kind === "choice" ? value.value : undefined}
              onChange={(optionId) => change({ kind: "choice", value: optionId })}
            />
          );
        case "multi_choice":
          return (
            <MultiChoiceQuestion
              question={asSingleQuestion(field, uniqueKey)}
              legend={label}
              optionLabelFor={optionLabelFor}
              value={
                value?.kind === "multi_choice"
                  ? value.value
                  : // A field re-kinded from choice (iron_supplements.timing)
                    // can still hold a legacy single answer; the first toggle
                    // overwrites it as multi_choice.
                    value?.kind === "choice"
                    ? [value.value]
                    : []
              }
              onChange={(selected) => change({ kind: "multi_choice", value: selected })}
            />
          );
        case "text":
          if (field.control === "photo_upload") {
            return (
              <PhotoUploadField
                labelledBy={fieldLabelId}
                questionKey={groupKey}
                value={value?.kind === "text" ? value.value : ""}
                onChange={(next) => change({ kind: "text", value: next })}
              />
            );
          }
          if (field.control === "supplement_picker" && productIdField) {
            const productIdValue = item.get(productIdField.key);
            return (
              <SupplementPicker
                labelledBy={fieldLabelId}
                name={value?.kind === "text" ? value.value : ""}
                productId={productIdValue?.kind === "text" ? productIdValue.value : ""}
                onNameChange={(next) => change({ kind: "text", value: next })}
                onProductIdChange={(next) => onFieldChange(productIdField.key, { kind: "text", value: next })}
                onDoseHintChange={doseHintFieldKey ? setDoseHint : undefined}
                category={field.catalogCategory}
                errorText={errorText}
              />
            );
          }
          return (
            <TextQuestion
              labelledBy={fieldLabelId}
              value={value?.kind === "text" ? value.value : ""}
              onChange={(next) => change({ kind: "text", value: next })}
              errorText={errorText}
            />
          );
        case "number":
          return (
            <NumberQuestion
              question={asSingleQuestion(field, uniqueKey)}
              labelledBy={fieldLabelId}
              unit={fieldCopy?.unit}
              helperText={field.key === doseHintFieldKey ? doseHint : undefined}
              value={value?.kind === "number" ? value.value : undefined}
              onChange={(next) => change({ kind: "number", value: next })}
              errorText={errorText}
            />
          );
        case "date":
          return (
            <DateQuestion
              labelledBy={fieldLabelId}
              value={value?.kind === "date" ? value.value : ""}
              onChange={(next) => change({ kind: "date", value: next })}
              errorText={errorText}
            />
          );
      }
    }

    return (
      <div key={field.key} className={s.field}>
        {/* Same reasoning as QuestionBlock's own label: multi_choice's
            fieldset/legend already carries the accessible name. */}
        {field.kind !== "multi_choice" ? (
          <Text as="p" size="lg" weight="bold" id={fieldLabelId} className={s.fieldLabel}>
            {label}
          </Text>
        ) : null}
        <div>{renderControl()}</div>
      </div>
    );
  }

  function renderTree(field: QuestionGroupField) {
    const block = renderField(field);
    const children = childrenByParent.get(field.key);
    if (!children) return block;
    return (
      <div key={field.key}>
        {block}
        {children.map((child) => (
          <ConditionalReveal
            key={child.key}
            show={reachable.has(child.key)}
            label={copy.groupFieldFor(groupKey, child.key)?.label ?? child.key}
            scrollContainer={scrollContainer}
          >
            {renderTree(child)}
          </ConditionalReveal>
        ))}
      </div>
    );
  }

  return <>{roots.map((field) => renderTree(field))}</>;
}
